/**
 * LiveOrderFeed — demo-only. Fires a subtle "new order" toast so a screen
 * recording visibly shows orders flowing in live, plus a tiny kiosk control to
 * change the cadence (Off / Slow / Normal / Fast) for different recording lengths.
 */
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Radio } from 'lucide-react';
import { QSR_STORES } from '@/components/dashboards/qsr/qsrDemoData';

const CHANNELS = [
  { label: 'Drive-Thru', color: '#f59e0b' },
  { label: 'Counter', color: '#10b981' },
  { label: 'Dine-in', color: '#0ea5e9' },
  { label: 'Uber Eats', color: '#06c167' },
  { label: 'DoorDash', color: '#ef4444' },
];
const ITEMS = ['Classic Grill Burger', 'Loaded Fries', 'Double Stack', 'Grilled Chicken Wrap', 'Kids Grill Box', 'Combo Meal'];

// cadence -> [minMs, maxMs] between orders (null = off)
const CADENCE = {
  off: null,
  slow: [45000, 70000],
  normal: [22000, 38000],
  fast: [8000, 15000],
};
const ORDER = ['off', 'slow', 'normal', 'fast'];
const LABEL = { off: 'Off', slow: 'Slow', normal: 'Normal', fast: 'Fast' };

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

function makeOrder() {
  const store = pick(QSR_STORES);
  const channel = pick(CHANNELS);
  const item = pick(ITEMS);
  const items = 1 + Math.floor(Math.random() * 3);
  const amount = (10 + Math.random() * 34).toFixed(2);
  return { store: store.name, storeId: store.id, channel, item, items, amount };
}

export default function LiveOrderFeed({ enabled = true, control = true }) {
  const [cadence, setCadence] = useState('normal');
  const timeoutRef = useRef(null);

  // Kiosk sync — when the tour's kiosk loop turns on, make sure orders are flowing.
  useEffect(() => {
    const onKiosk = (e) => {
      if (e.detail?.loop) setCadence((c) => (c === 'off' ? 'normal' : c));
    };
    window.addEventListener('qsr-kiosk-loop', onKiosk);
    return () => window.removeEventListener('qsr-kiosk-loop', onKiosk);
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    const range = CADENCE[cadence];
    if (!range) return undefined; // off
    let cancelled = false;

    const fire = () => {
      if (cancelled) return;
      const o = makeOrder();
      toast.custom(() => (
        <div
          data-testid="live-order-toast"
          className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-white/95 backdrop-blur px-4 py-3 shadow-lg min-w-[300px]"
        >
          <span className="relative flex h-2.5 w-2.5 shrink-0">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-75" style={{ backgroundColor: o.channel.color }} />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full" style={{ backgroundColor: o.channel.color }} />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-slate-900 leading-tight">
              New order · <span style={{ color: o.channel.color }}>{o.channel.label}</span> · {o.store}
            </p>
            <p className="text-[11px] text-slate-500 truncate">{o.item}{o.items > 1 ? ` +${o.items - 1} more` : ''}</p>
          </div>
          <span className="text-sm font-bold text-emerald-600 tabular-nums">${o.amount}</span>
        </div>
      ), { duration: 4000, position: (typeof window !== 'undefined' && window.innerWidth < 640) ? 'top-center' : 'bottom-right' });

      // Sync the hero "streaming now" counters with each order.
      window.dispatchEvent(new CustomEvent('qsr-live-order', {
        detail: { amount: parseFloat(o.amount), delivery: o.channel.label === 'Uber Eats' || o.channel.label === 'DoorDash', storeId: o.storeId },
      }));

      const [min, max] = range;
      timeoutRef.current = setTimeout(fire, min + Math.random() * (max - min));
    };

    // first order shortly after (re)start so a recording catches one early
    const [min] = range;
    timeoutRef.current = setTimeout(fire, Math.min(9000, min));

    return () => {
      cancelled = true;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [enabled, cadence]);

  if (!enabled || !control) return null;

  const cycle = () => setCadence((c) => ORDER[(ORDER.indexOf(c) + 1) % ORDER.length]);
  const isOff = cadence === 'off';

  return (
    <button
      type="button"
      onClick={cycle}
      data-testid="order-feed-cadence"
      title="Live order feed — tap to change speed"
      className="hidden sm:inline-flex fixed left-4 bottom-20 z-40 items-center gap-2 rounded-full border border-slate-200 bg-white/95 px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-lg backdrop-blur hover:bg-white transition-colors"
    >
      <span className="relative flex h-2 w-2">
        {!isOff && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-75" />}
        <span className={`relative inline-flex h-2 w-2 rounded-full ${isOff ? 'bg-slate-300' : 'bg-emerald-500'}`} />
      </span>
      <Radio className="w-3.5 h-3.5" />
      Live orders: <span className={isOff ? 'text-slate-400' : 'text-emerald-600'}>{LABEL[cadence]}</span>
    </button>
  );
}
